import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { adaptPaymentWerouteProps } from './PaymentWeroute.adapter';
import { PaymentWerouteProps } from './PaymentWeroute.types';
import { 
  selectUser, 
  selectIsUserLoggedIn, 
  selectIsAdminLoggedIn,
  registerCheckout,
  fetchWithcookieData,
  fetchCartItems,
  selectCartState,
  selectSelectedAddress,
  selectWithcookieData,
  searchAddress,
  selectAddress,
  selectAddressResults,
  selectAddressLoading
} from '../../../redux/WithcookieSlice';
import AddressSearchPopup from '../../common/AddressSearchPopup';
import { PriceDisplay } from '../../common/PriceDisplay';
import { useTranslation } from '../../../redux/TranslationHelper';
import './PaymentWeroute.css';

// 폼 데이터 타입 정의
interface FormData {
  name: string;
  phone: string;
  memo: string;
  zipcode: string;
  address: string;
  addressDetail: string;
  paymentMethod: string;
  agreeTerms: boolean;
}

interface CartItem {
  id: number;
  productId: number;
  product_id?: number;
  variantId?: number | null;
  variant?: {
    id: number;
    productId: number;
    sku: string;
    barcode?: string | null;
    additionalPrice: string;
    additionalPv: string;
    stock: number;
    options?: Array<{
      variantId: number;
      optionValueId: number;
      optionValue: {
        id: number;
        value: string;
        optionGroup: {
          id: number;
          name: string;
        };
      };
    }>;
  } | null;
  options?: Record<string, any> | null;
  product?: {
    id?: number;
    product_id?: number;
    product_name?: string;
    title?: string;
    name?: string;
    config?: {
      default_price?: number;
      discounted_price?: number;
      img_url?: string;
    };
    optionGroups?: Array<{
      id: number;
      name: string;
      code: string;
      optionValues?: Array<{
        id: number;
        value: string;
        code: string;
      }>;
    }>;
  };
  count: number;
}

/**
 * PaymentWeroute 결제 컴포넌트
 * Payment 컴포넌트와 동일한 UI로 Weroute 결제 처리
 */
const PaymentWeroute: React.FC<PaymentWerouteProps> = (props) => {
  // 속성 어댑터를 통해 props 변환
  const adaptedProps = adaptPaymentWerouteProps(props);
  const { t } = useTranslation();
  
  // 컴포넌트 속성 추출
  const { 
    className = "payment-component-wrapper padding-tb-40",
    style = {},
    data = {}
  } = adaptedProps;
  
  const {
    title = t("결제하기"),
    emptyText = t("장바구니가 비어 있습니다."),
    shippingFee = 0 // 기본 배송비
  } = data;
  
  // 에디터 모드 확인
  const isEditor = (props as any).isEditor || (props as any).isPreview;
  
  // Redux dispatch
  const dispatch = useDispatch() as any;
  
  // Redux state
  const cartState = useSelector(selectCartState);
  const cartItems = cartState.cartItems || [];
  const loading = cartState.loading;
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const selectedAddress = useSelector(selectSelectedAddress);
  const user = useSelector(selectUser);
  const withcookieData = useSelector(selectWithcookieData);
  const addressResults = useSelector(selectAddressResults);
  const addressLoading = useSelector(selectAddressLoading);
  
  // 상태 정의
  const [showAddressPopup, setShowAddressPopup] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    name: user?.name || '',
    phone: user?.phone || '',
    memo: '',
    zipcode: '',
    address: '',
    addressDetail: '',
    paymentMethod: 'CARD',
    agreeTerms: false
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [backgroundSearchTriggered, setBackgroundSearchTriggered] = useState(false);
  
  // 회사 설정에서 무료배송 기준금액 및 Weroute 키 가져오기
  const { freeShippingLimit, werouteKey, werouteMid } = useMemo(() => {
    try {
      // 1. company 객체에서 직접 가져오기 (최우선)
      const companyData = withcookieData?.company || withcookieData?.skin?.company;
      let werouteKeyFromCompany = companyData?.werouteKey;
      let werouteMidFromCompany = companyData?.werouteMid;
      
      // 2. companySettingJson에서 가져오기 (fallback)
      let companySettingJson = null;
      if (withcookieData?.company?.companySettingJson) {
        companySettingJson = withcookieData.company.companySettingJson;
      } else if (withcookieData?.skin?.company?.companySettingJson) {
        companySettingJson = withcookieData.skin.company.companySettingJson;
      }
      
      let settings = {};
      if (typeof companySettingJson === 'string') {
        settings = JSON.parse(companySettingJson);
      } else if (companySettingJson && typeof companySettingJson === 'object') {
        settings = companySettingJson;
      }
      
      return {
        freeShippingLimit: (settings as any).free_shipping_limit || 50000,
        werouteKey: werouteKeyFromCompany || (settings as any).weroute_key || null,
        werouteMid: werouteMidFromCompany || (settings as any).weroute_mid || null
      };
    } catch (error) {
      console.error(t('회사 설정 가져오기 오류:'), error);
      return {
        freeShippingLimit: 50000,
        werouteKey: null,
        werouteMid: null
      };
    }
  }, [withcookieData, t]);
  
  // 선택된 주소가 변경되면 폼 데이터 업데이트
  useEffect(() => {
    if (selectedAddress) {
      setFormData(prev => ({
        ...prev,
        address: selectedAddress.road || selectedAddress.jibun || '',
        zipcode: selectedAddress.postalCode || ''
      }));
    }
  }, [selectedAddress]);
  
  // 사용자 정보가 있으면 폼에 자동 입력 (백그라운드 주소 검색 포함)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        phone: user.phone || prev.phone
      }));
      
      // user.address가 있고 아직 백그라운드 검색이 트리거되지 않았으면 주소 검색 실행
      if (user.address && user.address.trim() && !backgroundSearchTriggered && !selectedAddress) {
        setBackgroundSearchTriggered(true); // 중복 실행 방지
        // 백그라운드에서 주소 검색 실행 (모달 없이)
        dispatch(searchAddress(user.address.trim()));
      }
    }
  }, [user, backgroundSearchTriggered, selectedAddress, dispatch]);
  
  // 백그라운드 주소 검색 결과 처리
  useEffect(() => {
    if (backgroundSearchTriggered && addressResults && addressResults.length > 0 && !addressLoading && !selectedAddress) {
      // 첫 번째 결과를 자동으로 선택
      const firstAddress = addressResults[0];
      dispatch(selectAddress(firstAddress));
    }
  }, [backgroundSearchTriggered, addressResults, addressLoading, selectedAddress, dispatch]);
  
  // 컴포넌트 마운트 시 장바구니 정보 로드
  useEffect(() => {
    if (isUserLoggedIn || isAdminLoggedIn) {
      dispatch(fetchCartItems());
      
      if (!withcookieData) {
        dispatch(fetchWithcookieData());
      }
    }
  }, [dispatch, isUserLoggedIn, isAdminLoggedIn, withcookieData]);
  
  // 등급/직급별 가격 계산 함수 (optionJson priority 기반)
  const calculateLevelPrice = useCallback((basePrice: number, product: any) => {
    // 로그인하지 않은 경우 또는 optionJson이 없는 경우
    if (!user || !isUserLoggedIn || !product?.optionJson?.priority) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    const priority = product.optionJson.priority;
    let levelPrice = basePrice;
    let levelName = null;
    let levelId: string | number | null = null;
    let levelPolicy = null;
    
    // priority에 따라 level1 또는 level2 사용
    if (priority === 'level1' && user.level1) {
      levelId = user.level1;
      levelPolicy = user.level1Policy;
      levelName = levelPolicy?.customName || levelPolicy?.type || '등급 할인';
      
      // level1_price 확인
      if (product.optionJson.level1_price && levelId !== null && product.optionJson.level1_price[levelId]) {
        levelPrice = Number(product.optionJson.level1_price[levelId]) || basePrice;
      }
    } else if (priority === 'level2' && user.level2) {
      levelId = user.level2;
      levelPolicy = user.level2Policy;
      levelName = levelPolicy?.customName || levelPolicy?.type || '직급 할인';
      
      // level2_price 확인
      if (product.optionJson.level2_price && levelId !== null && product.optionJson.level2_price[levelId]) {
        levelPrice = Number(product.optionJson.level2_price[levelId]) || basePrice;
      }
    }
    
    // 개별 가격이 없으면 supply_price_ratio 사용
    if (levelPrice === basePrice && levelPolicy?.settingJson?.supply_price_ratio) {
      const supplyPriceRatio = Number(levelPolicy.settingJson.supply_price_ratio) || 1;
      levelPrice = Math.floor(basePrice * supplyPriceRatio);
    } else if (levelPrice === basePrice && levelId) {
      // withcookieData에서 정책 찾기
      const levelPolicies = withcookieData?.skin?.company?.levelpolicies || withcookieData?.company?.levelpolicies || [];
      const matchingPolicy = levelPolicies.find((policy: any) => policy.id === levelId);
      if (matchingPolicy?.settingJson?.supply_price_ratio) {
        const supplyPriceRatio = Number(matchingPolicy.settingJson.supply_price_ratio) || 1;
        levelPrice = Math.floor(basePrice * supplyPriceRatio);
        if (!levelName && matchingPolicy) {
          levelName = matchingPolicy.customName || matchingPolicy.type || (priority === 'level1' ? '등급 할인' : '직급 할인');
        }
      }
    }
    
    const discount = basePrice - levelPrice;
    const discountRate = basePrice > 0 ? Math.round((discount / basePrice) * 100) : 0;
    
    return {
      originalPrice: basePrice,
      levelPrice,
      discount,
      discountRate,
      levelName: levelId ? levelName : null
    };
  }, [user, isUserLoggedIn, withcookieData]);

  // 상품 가격 합계 계산
  const calculateTotalPrice = useCallback((): number => {
    return cartItems.reduce((sum: number, item: CartItem) => {
      const basePrice = item.product?.config?.discounted_price 
        ? Number(item.product.config.discounted_price) 
        : item.product?.config?.default_price 
          ? Number(item.product.config.default_price) 
          : 0;
      
      // 직급별 가격 계산
      const priceInfo = calculateLevelPrice(basePrice, item.product);
      const finalBasePrice = priceInfo.levelPrice;
      
      // Variant 추가 가격 포함
      const additionalPrice = item.variant?.additionalPrice 
        ? Number(item.variant.additionalPrice) 
        : 0;
      
      const totalPrice = finalBasePrice + additionalPrice;
      
      return sum + (totalPrice * (item.count || 1));
    }, 0);
  }, [cartItems, calculateLevelPrice]);
  
  // 총 금액 계산 (상품 가격 + 배송비)
  const calculateGrandTotal = useCallback((): number => {
    const totalPrice = calculateTotalPrice();
    const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
    return totalPrice + appliedShippingFee;
  }, [calculateTotalPrice, shippingFee, freeShippingLimit]);
  
  // 폼 입력 처리
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);
  
  // 우편번호 검색 버튼 클릭 핸들러
  const handleAddressSearch = useCallback((): void => {
    setShowAddressPopup(true);
  }, []);
  
  // Weroute 결제 폼 제출 함수
  const submitWeroutePayment = (paymentData: any) => {
    // 동적으로 폼 생성
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://api.weroutefincorp.com/v2/pay/auth';
    form.acceptCharset = 'utf-8';
    form.style.display = 'none';
    
    // 필수 필드들을 폼에 추가
    const fields = {
      return_url: paymentData.return_url,
      user_agent: 'WP', // PC Web
      mid: paymentData.werouteMid, // 설정에서 가져온 MID (필수)
      ord_num: paymentData.ord_num,
      item_name: paymentData.item_name,
      amount: paymentData.amount,
      buyer_name: paymentData.buyer_name,
      buyer_phone: paymentData.buyer_phone,
      buyer_email: paymentData.buyer_email || '',
      is_send_email: '0', // 이메일 미발송
      installment: '0', // 일시불
      pay_key: paymentData.werouteKey, // 설정에서 가져온 PAY_KEY (필수)
      temp: paymentData.temp || ''
    };
    
    // 각 필드를 hidden input으로 추가
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(value);
      form.appendChild(input);
    });
    
    // 폼을 body에 추가하고 제출
    document.body.appendChild(form);
    form.submit();
    
    // 폼 제거 (선택사항)
    // document.body.removeChild(form);
  };
  
  // 결제 처리
  const handlePayment = useCallback(async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // 에디터 모드에서는 실제 결제를 진행하지 않음
    // if (isEditor) {
    //   alert(t('에디터 모드에서는 실제 결제가 진행되지 않습니다.'));
    //   return;
    // }
    
    // 폼 검증
    if (!formData.name || !formData.phone || !formData.address || !formData.zipcode) {
      alert(t('필수 입력 항목을 모두 입력해주세요.'));
      return;
    }
    
    if (!formData.agreeTerms) {
      alert(t('결제 및 개인정보 처리방침에 동의해주세요.'));
      return;
    }
    
    // 장바구니가 비어있는지 확인
    if (!cartItems || cartItems.length === 0) {
      alert(t('구매할 상품이 장바구니에 없습니다.'));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // 사용자 정보 확인
      if (!user || !user.id) {
        console.error(t("[결제 오류] 사용자 정보 없음:"), user);
        alert(t("로그인 정보를 확인할 수 없습니다. 다시 로그인해주세요."));
        return;
      }
      
      // Weroute 키 설정 확인
      if (!werouteKey || !werouteMid) {
        console.error(t("[결제 오류] Weroute 설정 없음:"), { werouteKey, werouteMid });
        alert(t("Weroute 결제 설정이 되어있지 않습니다. 관리자에게 문의해주세요."));
        return;
      }
      
      // 회사 정보가 없는 경우 새로 불러오기
      if (!withcookieData) {
        await dispatch(fetchWithcookieData());
      }
      
      // 회사 ID 가져오기
      const companyId = withcookieData?.company?.id || 
                       withcookieData?.skin?.companyId || 
                       withcookieData?.skin?.company?.id;
      
      if (!companyId) {
        console.error(t("[결제 오류] 회사 ID를 찾을 수 없습니다:"), withcookieData);
        alert(t('회사 정보를 불러올 수 없습니다. 페이지를 새로고침하거나 나중에 다시 시도해주세요.'));
        return;
      }
      
      // 주문 ID 생성
      const orderId = `ORDER_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      
      // 상품명 생성
      const firstItem = cartItems[0]?.product?.product_name || 
                       cartItems[0]?.product?.title || 
                       t('상품');
      const orderName = cartItems.length > 1 
        ? `${t(firstItem)} ${t('외')} ${cartItems.length - 1}${t('건')}` 
        : t(firstItem);
      
      const totalPrice = calculateTotalPrice();
      const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
      const grandTotal = totalPrice + appliedShippingFee;
      
      // 장바구니 아이템 구성
      const cartItems_formatted = cartItems.map((item: CartItem) => {
        let productId = item.product_id || item.product?.id || item.product?.product_id || item.id;
        
        if (!productId) {
          throw new Error(t("상품 정보가 올바르지 않습니다."));
        }
        
        return {
          product_id: Number(productId),
          company_id: companyId,
          user_id: user.id,
          count: item.count || 1
        };
      });
      
      // 장바구니 ID 목록
      const cartIds = cartItems.map(item => item.id).filter(id => id);
      
      // 주문 등록 데이터 준비
      const orderData = {
        user_id: user.id,
        company_id: companyId,
        skin_id: withcookieData?.skin?.id || 0,
        receiver_name: formData.name,
        receiver_phone: formData.phone,
        receiver_email: user?.email || '',
        memo: formData.memo || '',
        total_price: grandTotal,
        buy_price: grandTotal,
        parcel_price: totalPrice,
        payment_method: 'CARD',
        address: formData.address,
        address_detail: formData.addressDetail || '',
        postal_code: formData.zipcode,
        carts: cartItems_formatted,
        cart_ids: cartIds
      };
      
      // 주문 등록 API 호출
      const result = await dispatch(registerCheckout(orderData as any)).unwrap();
      
      if (!result?.checkout?.id) {
        throw new Error(result?.message || t('주문 등록에 실패했습니다.'));
      }
      
      const checkoutId = result.checkout.id;
      const registeredOrderId = `WEROUTE_${checkoutId}`;
      
      // checkoutId를 쿠키에 저장 (1시간 만료)
      const expires = new Date();
      expires.setTime(expires.getTime() + (60 * 60 * 1000)); // 1시간
      document.cookie = `weroute_checkout_id=${checkoutId}; expires=${expires.toUTCString()}; path=/`;
      
      // 전화번호 형식 정리 (하이픈 제거)
      const cleanPhone = formData.phone.replace(/[-\s]/g, '');
      
      const werouteRequestData = {
        return_url: `${window.location.origin}/payment/weroute/success`,
        ord_num: registeredOrderId,
        item_name: orderName,
        amount: String(grandTotal),
        buyer_name: formData.name,
        buyer_phone: cleanPhone,
        buyer_email: user?.email || '',
        temp: `checkout_id:${checkoutId}`,
        werouteKey: werouteKey,
        werouteMid: werouteMid
      };
      
      console.log('Weroute 결제 요청 데이터:', werouteRequestData);
      
      // Weroute 결제 폼 제출 (페이지가 바로 이동됨)
      submitWeroutePayment(werouteRequestData);
      return;
      
    } catch (error: any) {
      console.error(t('결제 오류]'), error);
      alert(t(`결제 처리 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`));
    } finally {
      setIsLoading(false);
    }
  }, [
    cartItems,
    calculateTotalPrice,
    dispatch,
    formData,
    isEditor,
    shippingFee,
    t,
    user,
    withcookieData,
    freeShippingLimit,
    werouteKey,
    werouteMid,
    submitWeroutePayment
  ]);
  
  // 로딩 상태 표시
  if (loading) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="payment-loading">
                <p>{t("장바구니 정보를 불러오는 중입니다...")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // 로그인 필요
  if (!isUserLoggedIn && !isAdminLoggedIn && !isEditor) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="payment-login-required">
                <p>{t("결제를 진행하려면 로그인이 필요합니다.")}</p>
                <a href="/login" className="login-button">{t("로그인")}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // 장바구니가 비어있는 경우
  if (!cartItems || cartItems.length === 0) {
    if (!isEditor) {
      return (
        <div className={className} style={style}>
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="payment-empty">
                  <h2>{title}</h2>
                  <p>{emptyText}</p>
                  <a href="/shopping" className="continue-shopping-button">
                    {t("쇼핑 계속하기")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }
  
  const totalPrice = calculateTotalPrice();
  const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
  const grandTotal = totalPrice + appliedShippingFee;
  
  return (
    <div className={className} style={style}>
      {showAddressPopup && (
        <AddressSearchPopup 
          onClose={() => setShowAddressPopup(false)} 
          initialSearchQuery={formData.address || ''}
        />
      )}
      
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h2 className="payment-title">{title}</h2>
          </div>
        </div>
        
        <div className="row" style={{ margin:"0 auto" }}>
          {/* 주문 정보 및 결제 폼 */}
          <div className="col-lg-8 col-md-12">
            <div className="payment-form-wrapper">
              <h3 className="form-section-title">{t("주문자 정보")}</h3>
              <form onSubmit={handlePayment} className="payment-form">
                <div className="form-group">
                  <label htmlFor="name">{t("이름")} <span className="required">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">{t("휴대폰 번호")} <span className="required">*</span></label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder={t("010-0000-0000")}
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="memo">{t("메모")}</label>
                  <textarea
                    id="memo"
                    name="memo"
                    value={formData.memo}
                    onChange={(e) => handleInputChange(e as any)}
                    className="form-control"
                    rows={3}
                    placeholder={t("주문 시 요청사항을 입력해주세요")}
                  />
                </div>

                <h3 className="form-section-title mt-4">{t("배송지 정보")}</h3>
                
                <div className="form-group">
                  <label htmlFor="zipcode">{t("우편번호")} <span className="required">*</span></label>
                  <div className="zipcode-group">
                    <input
                      type="text"
                      id="zipcode"
                      name="zipcode"
                      value={formData.zipcode}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                      readOnly
                    />
                    <button 
                      type="button" 
                      className="search-zipcode-btn"
                      onClick={handleAddressSearch}
                    >
                      {formData.address ? t("주소 변경") : t("우편번호 찾기")}
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">{t("주소")} <span className="required">*</span></label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                    readOnly
                  />
                  {formData.address && (
                    <small className="form-text text-muted">
                      {t("주소를 변경하려면 '주소 변경' 버튼을 클릭하세요.")}
                    </small>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="addressDetail">{t("상세주소")}</label>
                  <input
                    type="text"
                    id="addressDetail"
                    name="addressDetail"
                    value={formData.addressDetail}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <h3 className="form-section-title mt-4">{t("결제 수단")}</h3>
                
                <div className="payment-method-info">
                  <div className="payment-method-selected">
                    <div className="payment-method-icon">
                      <i className="fas fa-credit-card"></i>
                    </div>
                    <div>
                      <strong>{t("카드결제")}</strong>
                      <p>{t("안전하고 빠른 카드결제 서비스")}</p>
                    </div>
                  </div>
                </div>

                <div className="terms-agreement mt-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                      className="form-check-input"
                      required
                    />
                    <label htmlFor="agreeTerms" className="form-check-label">
                      {t("결제 진행 및 개인정보 처리방침에 동의합니다")} <span className="required">*</span>
                    </label>
                  </div>
                </div>

                <div className="payment-button-container mt-4">
                  <button type="submit" className="payment-button" disabled={isLoading}>
                    {isLoading ? (
                      t("처리 중...")
                    ) : (
                      <>
                        {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                        <PriceDisplay price={grandTotal} /> {t("결제하기")}
                      </>
                    )}
                  </button>
                </div>
                
               
              </form>
            </div>
          </div>
          
          {/* 주문 요약 정보 */}
          <div className="col-lg-4 col-md-12">
            <div className="order-summary">
              <h3 className="summary-title">{t("주문 내역")}</h3>
              
              <div className="order-items">
                {cartItems && cartItems.length > 0 ? (
                  <ul className="order-item-list">
                    {cartItems.map((item: CartItem, index: number) => (
                      <li key={index} className="order-item">
                        <div className="order-item-image">
                          {item.product?.config?.img_url ? (
                            <img
                              src={item.product.config.img_url}
                              alt={item.product.title || '상품 이미지'}
                            />
                          ) : (
                            <div className="no-image">{t("이미지 없음")}</div>
                          )}
                        </div>
                        <div className="order-item-info">
                          <p className="item-name">
                            {item.product?.title ? t(item.product.title) : t('상품명 없음')}
                          </p>
                          
                          {/* 옵션 정보 표시 */}
                          {item.variant && item.variant.options && item.variant.options.length > 0 && (
                            <p className="item-options">
                              {item.variant.options.map((opt, index) => (
                                <span key={index}>
                                  {opt.optionValue.optionGroup.name}: {opt.optionValue.value}
                                  {index < (item.variant?.options?.length || 0) - 1 && ', '}
                                </span>
                              ))}
                            </p>
                          )}
                          
                          {/* 레거시 옵션 정보 표시 */}
                          {!item.variant && item.options && Object.keys(item.options).length > 0 && (
                            <p className="item-options">
                              {(() => {
                                const optionEntries = Object.entries(item.options)
                                  .filter(([key]) => !['sku', 'price', 'pv'].includes(key));
                                
                                if (optionEntries.length === 0) return null;
                                
                                return optionEntries.map(([key, value], index) => (
                                  <span key={key}>
                                    {key}: {value}
                                    {index < optionEntries.length - 1 && ', '}
                                  </span>
                                ));
                              })()}
                            </p>
                          )}
                          
                          <p className="item-quantity">
                            {t("수량")}: {item.count || 1}{t("개")}
                          </p>
                          <p className="item-price">
                            {(() => {
                              const basePrice = item.product?.config?.discounted_price 
                                ? Number(item.product.config.discounted_price) 
                                : item.product?.config?.default_price 
                                  ? Number(item.product.config.default_price) 
                                  : 0;
                              
                              // 직급별 가격 계산
                              const priceInfo = calculateLevelPrice(basePrice, item.product);
                              const finalBasePrice = priceInfo.levelPrice;
                              
                              const additionalPrice = item.variant?.additionalPrice 
                                ? Number(item.variant.additionalPrice) 
                                : 0;
                              
                              const totalPrice = (finalBasePrice + additionalPrice) * (item.count || 1);
                              
                              return (
                                <>
                                  {/* 직급별 가격이 있는 경우 */}
                                  {priceInfo.discount > 0 && priceInfo.levelName && (
                                    <span className="level-info-small">
                                      <span className="level-badge-tiny">{priceInfo.levelName}</span>
                                    </span>
                                  )}
                                  {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                                  <PriceDisplay price={totalPrice} />
                                  {additionalPrice > 0 && (
                                    <span className="additional-price-info">
                                      (옵션 +₩{additionalPrice.toLocaleString()})
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-items">{t("주문 내역이 없습니다.")}</p>
                )}
              </div>
              
              <div className="order-totals">
                <div className="order-total-item">
                  <span>{t("상품 금액")}</span>
                  <span>
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                    <PriceDisplay price={totalPrice} />
                  </span>
                </div>
                <div className="order-total-item">
                  <div className="item-label">{t("배송비")}</div>
                  <div className="item-value">
                    {totalPrice >= freeShippingLimit ? (
                      <span className="free-shipping">{t("무료배송")}</span>
                    ) : (
                      /* @ts-ignore - PriceDisplay는 price prop을 받는 컴포넌트입니다 */
                      <PriceDisplay price={appliedShippingFee} />
                    )}
                  </div>
                </div>
                <div className="order-total-item grand-total">
                  <span>{t("총 결제금액")}</span>
                  <span>
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                    <PriceDisplay price={grandTotal} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentWeroute;

// Named export 추가
export { PaymentWeroute as PaymentWerouteComponent };